/* eslint-disable prettier/prettier */
import {
  Component,
  PrintArea,
  PrintAreaCustomization,
  ProductCustomization,
  WarehouseItem,
} from "@/models";
import WarehouseItemCustomization from "@/models/WarehouseItemCustomization";
import CustomizationPrintArea from "@/models/PrintAreaCustomization";
import Product from "@/models/Product";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import productCustomizationService from "@/services/productCustomizationService";
import printAreaCustomizationService from "@/services/printAreaCustomizationService";
import { easync } from "@/helpers/http";
import { convertBase64ToBlob, loadImage } from "@/helpers/customizerHelper";
import productService from "@/services/productService";
import { currentUserStore, onboardingStore } from "@/store/typed";
import { scaleOutputJson } from "@/helpers/customizerHelper";
import { CONFIGURATOR_HISTORY_SIZE } from "@/constants";
import {showError} from "@/helpers/Toast";
import {I18N_MESSAGES} from "@/utils/i18n";

@Module({
  stateFactory: true,
  namespaced: false,
  name: "customizer",
})
export default class Customizer extends VuexModule {
  private _product: Product | null = null;
  private _ratio = 1;
  private _productCustomization: ProductCustomization | null = null;
  private _currentWarehouseItemCustomization: WarehouseItemCustomization | null =
    null;
  private _currentPrintAreaCustomization: CustomizationPrintArea | null = null;
  private _navigationLocked = false;
  private _mode: "2d" | "3d" = "2d";
  private _editMode = false;
  private _canvases = [];
  private _previewMode = false;
  private _zoomEnabled = true;

  get product(): Product {
    return this._product;
  }

  get canvases(): any {
    return this._canvases;
  }
  get zoomEnabled(): boolean{
    return  this._zoomEnabled;
  }

  get canvasByPrintAreaCustomization(): any {
    return (id: string) =>
      this._canvases.find((o: any) => o.printAreaCustomization.id == id);
  }

  get productCustomization(): ProductCustomization {
    return this._productCustomization;
  }

  get navigationLocked(): boolean {
    return this._navigationLocked;
  }

  get printAreaCustomizations(): PrintAreaCustomization[] {
    return this.productCustomization.warehouseItemCustomizations
      .map((o: WarehouseItemCustomization) => o.printAreaCustomizations)
      .flat();
  }

  get warehouseItemCustomizations(): WarehouseItemCustomization[] {
    return this._productCustomization.warehouseItemCustomizations;
  }

  get currentWarehouseItemCustomization(): WarehouseItemCustomization {
    return this._currentWarehouseItemCustomization;
  }

  get mode(): "2d" | "3d" {
    return this._mode;
  }

  get editMode(): boolean {
    return this._editMode;
  }

  get currentPrintAreaCustomization(): CustomizationPrintArea {
    return this._currentPrintAreaCustomization;
  }

  get currentOutputHistory(): any {
    return this.currentPrintAreaCustomization.outputHistory;
  }

  get currentComponent(): Component | null {
    const component = this.components.find(
      (item: Component) =>
        item.id?.toString() ===
        this._currentPrintAreaCustomization?.printArea?.componentId?.toString()
    );
    return component || null;
  }

  get components(): Component[] {
    return this.warehouseItem.warehouseItemArtifact.components;
  }

  get componentByCode(): any {
    return (code: string) =>
      this.components.find((item: Component) => item.code === code) || null;
  }

  get warehouseItem(): WarehouseItem {
    return this._currentWarehouseItemCustomization?.warehouseItem || null;
  }

  get ratio(): number {
    return this._ratio;
  }

  get printArea(): PrintArea {
    return this._currentPrintAreaCustomization?.printArea || null;
  }

  get updateable(): boolean {
    return this._productCustomization.updateable && !this._previewMode;
  }

  get printAreaCustomizationByUuid() {
    return (uuid: string) =>
      this.warehouseItemCustomizations.find((whi) => whi.uuid === uuid)
        ?.printAreaCustomizations[0] || null;
  }

  get allItemsConfigured(): boolean {
    const allConfigured = this.warehouseItemCustomizations.every(
      (wiCust: WarehouseItemCustomization) =>
        wiCust.printAreaCustomizations.every(
          (paCust: PrintAreaCustomization) => paCust.completed
        )
    );

    return allConfigured || false;
  }

  @Mutation
  setRatio(ratio: number) {
    this._ratio = ratio;
  }

  @Mutation
  setZoomEnabled(zoomEnabled: boolean) {
    this._zoomEnabled = zoomEnabled;
  }

  @Mutation
  setPreviewMode(previewMode: boolean) {
    this._previewMode = previewMode;
  }

  @Mutation
  setEditMode(editMode: boolean) {
    this._editMode = editMode;
  }

  @Mutation
  setNavigationLocked(loaded: boolean) {
    this._navigationLocked = loaded;
  }

  @Mutation
  setProductCustomization(payload: ProductCustomization) {
    this._productCustomization = payload;
    this._currentWarehouseItemCustomization =
      this._productCustomization.warehouseItemCustomizations[0];
    this._currentPrintAreaCustomization =
      this._currentWarehouseItemCustomization.printAreaCustomizations[0];
  }

  @Mutation
  setMode(payload: "2d" | "3d") {
    this._mode = payload;
  }

  @Mutation
  setProduct(payload: Product) {
    this._product = payload;
    if (this._productCustomization) {
      this._currentWarehouseItemCustomization =
        this._productCustomization.warehouseItemCustomizations[0];
      this._currentPrintAreaCustomization =
        this._currentWarehouseItemCustomization.printAreaCustomizations[0];
    }
  }

  @Mutation
  setWarehouseItemCustomization(payload: WarehouseItemCustomization) {
    this._currentWarehouseItemCustomization = payload;
    const ids =
      this._currentWarehouseItemCustomization.printAreaCustomizations.map(
        (item) => item.id
      );
    if (!ids.includes(this._currentPrintAreaCustomization?.id)) {
      this._currentPrintAreaCustomization =
        this._currentWarehouseItemCustomization.printAreaCustomizations[0];
    }
  }

  @Mutation
  setPrintAreaCustomization(printAreaCustomization: PrintAreaCustomization) {
    this._currentPrintAreaCustomization =
      this._currentWarehouseItemCustomization.printAreaCustomizations.find(
        (o) => o.id === printAreaCustomization.id
      );
  }

  @Mutation
  setProductCustomizationTerms(accepted: boolean) {
    
    this._productCustomization.termsAccepted = accepted;
  }

  @Mutation
  updateOutput(payload: any) {
    if (payload != this._currentPrintAreaCustomization.lastHistoryItem) {
      if (
        this._currentPrintAreaCustomization.outputHistory.length >=
        CONFIGURATOR_HISTORY_SIZE
      ) {
        this._currentPrintAreaCustomization.outputHistory.shift();
      }
      this._currentPrintAreaCustomization.outputHistory.push({
        ...this._currentPrintAreaCustomization.output,
      });
    }
    this._currentPrintAreaCustomization.output = payload;
  }

  @Mutation
  updateSvg(payload: any) {
    this._currentPrintAreaCustomization.svg = payload;
  }

  @Action
  async destroyCustomization(){
    const [data, error] = await easync(
      productCustomizationService.deleteProductCustomization(this._productCustomization.uuid)
    );
  }

  @Mutation
  async updatedLastCustomizedAt(){
    await easync(
      productCustomizationService.setProductCustomizationCustomizing({id: this._productCustomization?.uuid})
    );
  }

  @Mutation
  rollbackOutput(): boolean {
    const currentHistory = this._currentPrintAreaCustomization.outputHistory;
    const lastHistory = currentHistory[currentHistory.length - 1];
    if (lastHistory) {
      this._currentPrintAreaCustomization.output =
        this._currentPrintAreaCustomization.outputHistory.pop();
      return true;
    }
    return false;
  }

  @Action({ rawError: true })
  async createProductCustomization(product: Product) {
    const [data, error] = await easync(
      productCustomizationService.createProductCustomization({
        productId: product.id,
      })
    );
    if (data) {
      onboardingStore.setOnboarding(false)
      onboardingStore.setTextOnboarding(true);
      onboardingStore.setBackgroundOnboarding(true);
      this.context.commit("setProductCustomization", data);
    } else {
      // TODO: Handle it
      console.log(error);
    }
  }

  @Action({ rawError: true })
  async _setProduct(payload: {
    id: string;
    reloadCustomization: boolean;
    productCustomizationId?: string;
  }) {
    const product = await productService.getProduct(payload.id);
    this.context.commit("setProduct", product);
    if (payload.productCustomizationId) {
      const [data, error] = await easync(
        productCustomizationService.getProductCustomization(
          payload.productCustomizationId
        )
      );
      console.log(data);
      if (data) {
        // if (data.updateable) {
          // this.context.commit("setOnboarding", false);
          this.context.commit("setProductCustomization", data);
        // }
      } else {
        showError(I18N_MESSAGES[currentUserStore.lang].warnings.load_failed)
      }
    } else if (
      !this.productCustomization ||
      this.productCustomization.productId.toString() != product.id.toString()
    ) {
      await this.createProductCustomization(product);
    } else if (payload.reloadCustomization) {
      const [data, error] = await easync(
        productCustomizationService.getProductCustomization(
          this.productCustomization.uuid
        )
      );
      console.log(data);

      if (data) {
        if (data.updateable && !this._previewMode) {
         onboardingStore.setOnboarding(false);
          this.context.commit("setProductCustomization", data);
        } else {
          await this.createProductCustomization(product);
        }
      } else {
        if((error.toJSON() as any).message.includes('404')){
          await this.createProductCustomization(product);
        }
        console.log(error);
      }
    }
    await this.context.commit("updatedLastCustomizedAt");
  }

  @Action({ rawError: true })
  _updatePrintAreaCustomizationOutput(output: string) {
    this.context.commit("updateOutput", output);
  }

  @Action({ rawError: true })
  async _uploadPicture(image: any): Promise<string> {
    const formData = new FormData();  
    const blob =  convertBase64ToBlob(image);
    const img: any = await loadImage(image);
    formData.append("file", blob);
    if(this._currentPrintAreaCustomization.colorProfile=='greyscale'){
      formData.append("greyscale", "true");
    }
    formData.append("width", img.width);
    formData.append("height", img.height);
    const [data, error] = await easync(
      productCustomizationService.uploadProductCustomization(
        this._productCustomization.uuid,
        formData
      )
    );
    if (data) {
      return data.url;
    } else {
      showError(I18N_MESSAGES[currentUserStore.lang].warnings.upload_failed)
      console.log(error);
    }
    return null;
  }

  @Action({ rawError: true })
  async _uploadOutputSvgAsync(updateData: {
    svg: string;
    output: any;
    printAreaCustomization: PrintAreaCustomization
  }): Promise<string> {

    if(!this.updateable){
      return;
    }

    const output = JSON.parse(
      JSON.stringify(updateData.output)
    );

    const scaledOutput = scaleOutputJson(output, updateData.printAreaCustomization.deviceRatio);
    
    const payload = {
      output: JSON.stringify(scaledOutput),
      svg: updateData.svg,
    };

    const [data, error] = await easync(
      printAreaCustomizationService.uploadPrintAreaCustomizationAsync(
        updateData.printAreaCustomization.uuid,
        payload
      )
    );
    
    if (data) {
      const printAreaCustomization = this.printAreaCustomizations.find((pac: PrintAreaCustomization) => pac.id === updateData.printAreaCustomization.id)
      printAreaCustomization.imageOutput = data.imageOutput;
      return data.url;
    } else {
      showError(I18N_MESSAGES[currentUserStore.lang].warnings.update_failed)
      console.log(error);
    }
    return null;
  }
}
